import { useEffect, useRef, useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';

type Props = {
  children: React.ReactNode;
  offset: number;
  initialWidth: number;
  maxWidth?: number;
  minWidth?: number;
  disabled?: boolean;
  hideContent?: boolean;
  onChange?: (width: number) => void;
  onResizeStart?: (width: number) => void;
  onResizeStop?: (width: number) => void;
  className?: string;
  onDoubleClick?: (newWidth: number) => void;
};

const DraggableHandle = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  width: 4px;
  padding: 4px 0 0;
  top: 0;
  bottom: 0;
  right: 0;
  cursor: col-resize;
  z-index: var(--zindex-layer-09);
  height: 100%;
  transition: all var(--animation-duration-medium);

  &:hover {
    background-color: var(--blue-50);
    user-select: none;
  }
`;

const Wrapper = styled.div<{
  itemWidth: number;
  hideContent: boolean;
  isResizing: boolean;
}>`
  position: relative;
  width: ${(props) => (props.hideContent ? '0px' : `${props.itemWidth}px`)};
  height: 100%;
  user-select: none;
  /* Block transitons on width when resizing */
  transition: ${(props) => props.isResizing && 'width 0s !important'};
`;

export const Resizable = (props: Props) => {
  const {
    children,
    offset,
    maxWidth,
    minWidth,
    disabled = false,
    initialWidth,
    hideContent = false,
    onResizeStart,
    onResizeStop,
    onChange,
    onDoubleClick: onDoubleClickProp,
    className,
  } = props;
  const [isResizing, setIsResizing] = useState(false);
  const [width, setWidth] = useState(initialWidth);

  // Use refs to store latest values without triggering re-renders
  const widthRef = useRef(width);
  const onChangeRef = useRef(onChange);
  const onResizeStopRef = useRef(onResizeStop);

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onResizeStopRef.current = onResizeStop;
  }, [onResizeStop]);

  const onMouseDown = () => {
    setIsResizing(true);
    onResizeStart?.(widthRef.current);
  };

  useEffect(() => {
    setWidth(initialWidth);
  }, [initialWidth]);

  const onDoubleClick = () => {
    if (onDoubleClickProp) {
      onDoubleClickProp(
        maxWidth && width < maxWidth
          ? maxWidth
          : minWidth && width === maxWidth
          ? minWidth
          : width,
      );

      return;
    }
    if (maxWidth && width < maxWidth) {
      setWidth(maxWidth);
      onChange?.(maxWidth);

      return;
    }
    if (minWidth && width === maxWidth) {
      setWidth(minWidth);
      onChange?.(minWidth);
    }
  };

  useEffect(() => {
    const onMouseUp = () => {
      setIsResizing(false);
      onResizeStopRef.current?.(widthRef.current);
    };

    const onMouseMove = (e) => {
      if (isResizing) {
        const value = Math.min(
          Math.max(e.clientX - offset + 2, minWidth ?? 0),
          maxWidth ?? Infinity,
        );

        setWidth(() => {
          return value;
        });
        onChangeRef.current?.(value);
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [isResizing, offset, minWidth, maxWidth]);

  return (
    <Wrapper
      itemWidth={width ?? 256}
      hideContent={hideContent}
      data-resizing={isResizing}
      isResizing={isResizing}
      className={
        className ? `${className} resizable-wrapper` : 'resizable-wrapper'
      }
    >
      {children}
      {!disabled ? (
        <DraggableHandle
          onMouseDown={onMouseDown}
          onDoubleClick={onDoubleClick}
        />
      ) : null}
    </Wrapper>
  );
};
